import React from 'react';
import { Form } from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';
import { FormItem } from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { useTheme } from 'styled-components';
import { ItemIdentifierType, ProductCodeType } from '__generated__/graphql';
import {
  StyledFieldRow,
  StyledAddonSelect,
  StyledInput,
  StyledRemoveButton,
} from './styles';

/**
 * Combined options for product identifiers and product codes.
 * ItemIdentifiers: SE (Serial Number), MN (Model Number), RN (Registered Number), ALT (Alternate)
 * ProductCodes: SKU (Stock Keeping Unit)
 */
const IDENTIFIER_TYPE_OPTIONS = [
  { value: ProductCodeType.SKU, label: 'SKU', title: 'Stock Keeping Unit' },
  { value: ItemIdentifierType.SE, label: 'SN', title: 'Serial Number' },
  { value: ItemIdentifierType.MN, label: 'MN', title: 'Model Number' },
  { value: ItemIdentifierType.RN, label: 'RN', title: 'Registered Number' },
  {
    value: ItemIdentifierType.ALT,
    label: 'ALT',
    title: 'Alternate Identifier',
  },
];

// Get max length based on identifier type. SKU allows 19 chars, others allow 17.
const getMaxLength = (type?: string): number =>
  type === ProductCodeType.SKU ? 19 : 17;

export interface ProductIdentifierFieldsProps {
  localNamePath: NamePath;
  readOnly?: boolean;
  disabled?: boolean;
  onRemove?: () => void;
  showRemove?: boolean;
}

const ProductIdentifierFields: React.FC<ProductIdentifierFieldsProps> = ({
  localNamePath,
  readOnly = false,
  disabled = false,
  onRemove,
  showRemove = true,
}) => {
  const theme = useTheme();
  const form = Form.useFormInstance();
  const identifierType = Form.useWatch([...localNamePath, 'type'], form);

  return (
    <StyledFieldRow>
      <FormItem
        name={[...localNamePath, 'value']}
        $itemSize={theme.size.short_medium}
        $readOnly={readOnly}
      >
        <StyledInput
          addonBefore={
            <Form.Item name={[...localNamePath, 'type']} noStyle>
              <StyledAddonSelect
                options={IDENTIFIER_TYPE_OPTIONS}
                disabled={disabled || readOnly}
                placeholder="Type"
                allowClear
              />
            </Form.Item>
          }
          maxLength={getMaxLength(identifierType)}
          disabled={disabled || readOnly}
          placeholder="Enter identifier"
        />
      </FormItem>
      {showRemove && !readOnly && (
        <StyledRemoveButton
          type="text"
          danger
          icon={<MinusCircleOutlined />}
          onClick={onRemove}
          disabled={disabled}
        >
          Remove
        </StyledRemoveButton>
      )}
    </StyledFieldRow>
  );
};

export default ProductIdentifierFields;
